import NavigationButtons from "../components/NavigationButtons";
import { useAuthState } from "../hooks/useAuth";
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Button from "../components/Button";
import { useToasts } from "../hooks/useToasts";
import { contactAPI } from "../services/api";
import "./Contact.css";

const Contact = () => {
  const { user } = useAuthState();
  const { addToast } = useToasts();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const messageRef = React.useRef(null);

  useEffect(() => {
    const subjectParam = searchParams.get("subject");
    if (subjectParam) {
      setFormData((prev) => ({
        ...prev,
        subject: subjectParam,
      }));
    }
  }, [searchParams]);

  const autoResizeMessage = React.useCallback(() => {
    const textarea = messageRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, []);

  useEffect(() => {
    autoResizeMessage();
  }, [formData.message, autoResizeMessage]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      addToast("Please fix the errors in the form", "error-warning");
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      await contactAPI.sendMessage(
        formData.name,
        formData.email,
        formData.subject,
        formData.message,
      );

      addToast("Message sent successfully!", "success-info");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      if (error.response?.status === 429) {
        addToast(
          "Too many messages sent. Please wait before sending another.",
          "error-warning",
        );
      } else {
        addToast(
          error.response?.data?.details?.[0] ||
            "Error sending message. Please try again.",
          "error-warning",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-container">
      {user && <NavigationButtons />}
      <h1 className="contact-title">Contact</h1>

      <div className="contact-content">
        <div className="contact-column">
          <div className="contact-text">
            <div className="contact-section">
              <h3 className="contact-heading">Get in Touch</h3>
              <div className="contact-info">
                <p>
                  <strong>Email:</strong> contact@asasiasa.com
                </p>
              </div>
            </div>

            <div className="contact-section">
              <h3 className="contact-heading">Tell Us What You Think</h3>
              <p className="contact-paragraph">
                Use the contact form or email us directly — we'd genuinely like
                to hear from you. Tell us what you like, what doesn't work for
                you, and what you'd change. If there's a feature you wish
                existed, or something that feels confusing or unnecessary, let
                us know.
              </p>
              <p className="contact-paragraph">We're especially interested in:</p>
              <ul className="contact-list">
                <li>what you enjoy using most</li>
                <li>what slows you down or frustrates you</li>
                <li>features you'd like to see added</li>
                <li>things you'd prefer done differently</li>
              </ul>
              <p className="contact-paragraph">
                Every message helps us improve. We read everything and aim to
                respond as soon as possible.
              </p>
            </div>
          </div>
        </div>

        <div className="contact-column">
          <div className="contact-form-container">
            <h3 className="contact-heading">Send us a message</h3>

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="contact-form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="contact-form-input"
                />
                {errors.name && (
                  <span className="contact-error-message">{errors.name}</span>
                )}
              </div>

              <div className="contact-form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="contact-form-input"
                />
                {errors.email && (
                  <span className="contact-error-message">{errors.email}</span>
                )}
              </div>

              <div className="contact-form-group">
                <label htmlFor="subject">Subject *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="contact-form-input"
                />
                {errors.subject && (
                  <span className="contact-error-message">
                    {errors.subject}
                  </span>
                )}
              </div>

              <div className="contact-form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => {
                    handleChange(e);
                    autoResizeMessage();
                  }}
                  rows="2"
                  className="contact-form-textarea"
                  ref={messageRef}
                />
                {errors.message && (
                  <span className="contact-error-message">
                    {errors.message}
                  </span>
                )}
              </div>

              <div className="form-buttons">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="button-btn button-btn-blue"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
